/**
 * One surface for adding food, raised over the day it writes into.
 *
 * The app had two ways to log and they behaved differently. The field under
 * each meal on Today wrote instantly and showed its results in place; `/add`
 * was a whole route with four tabs, a basket that wrote nothing until you
 * pressed Add, and a second route behind it for the camera. Which one you used
 * decided whether your food was logged, whether it survived leaving the screen,
 * and whether the ring moved — so "add food" was not one thing the app did, it
 * was two things that disagreed.
 *
 * This is the merge. A bottom sheet, raised by the `+` under a meal, opening on
 * that meal's **usuals**, writing every item the instant it is tapped, with an
 * undo tray instead of a basket. Scan is a mode of it rather than a route.
 * Cooking a pot is the opposite: it is a route (`/prepare/new`), because it
 * logs nothing and a draft that died with the sheet was the most expensive
 * Escape in the app. The Stored rail here eats from a pot and opens it;
 * everything else about pots lives under Prepare.
 * There is no `/add` any more, and `pages/add-draft.ts` went with it: a basket
 * that had to survive being paged sideways off a destination was solving a
 * problem created by making the add a destination.
 *
 * ## The rules it is built on
 *
 * **Every tap writes.** Through the offline queue, exactly as the inline field
 * did — the row appears instantly, survives a dead connection, and the ring
 * moves. The tray is the receipt. Nothing here is a draft, so nothing here can
 * be lost by leaving.
 *
 * **A food can go on the plate twice, and the sheet asks rather than decides.**
 * The pad used to fold a second weight of a food already in the meal into the
 * row that was there — a correction, on the theory that "the same thing again"
 * is rarer than "that was 180 g, not 100". It is not rare: the second coffee,
 * the second slice, the top-up from the pan. So every write is a row, and when
 * one lands beside another of the same food the tray offers to merge them into
 * one at the sum. One tap if that is what was meant, none if it was not, and
 * nothing is decided on the user's behalf either way. Corrections live where
 * the row does — on Today, where opening it re-weighs it.
 *
 * **A usual is a meal, not a food.** The row at the top offers platefuls — the
 * foods this meal keeps coming back to *together*, at the weights they were
 * last eaten at — and taking one opens it as a list you can re-weigh, drop a
 * line from, and then add in one go. Ranked single foods were Recent with a
 * second sort on it, which is why the two sections used to read as one thing
 * said twice. This is the one place in the sheet where a tap does not write
 * immediately, and it earns that: a plateful is four decisions at once, and the
 * pane is where you disagree with one of them before committing the rest.
 *
 * **One surface, one input at a time.** Grams are typed on the sheet's own pad
 * (`GramsPad`), never on the system keyboard; the system keyboard is for words
 * and multi-field forms. Opening either dismisses the other, so the sheet is
 * never taller than the screen. A 560px sheet plus a 292px keyboard is 852px on
 * an 812px phone, and the arithmetic does not care how the layout is written.
 *
 * **The keyboard resizes the sheet rather than pushing it.** iOS Safari does
 * not shrink the layout viewport when the keyboard opens, so `position: fixed;
 * bottom: 0` puts the sheet *behind* it. `visualViewport` is measured instead —
 * see `lib/keyboard.ts` — and the sheet takes the space above it.
 *
 * **Recent is the only vertical scroller.** The usuals row scrolls sideways and
 * the docked field never moves, so the list keeps its place while you work and
 * the field is never more than a thumb away. A plateful taken off that row
 * scrolls in the same place, for the same reason.
 *
 * **Recent is ordered by the meal the sheet is open on.** The foods this meal
 * is made of come first, in the order they are usually logged — onions, then
 * oil — because that is the order the next one will be typed in. Sorted purely
 * by the clock, the list hands yesterday's lunch back *backwards*, and rebuilding
 * it means reading four rows bottom-up while the sheet claims to be the fast
 * path. The sort is the server's, not this file's: it has to hold across pages.
 */

import {
  forGrams,
  forGramsNutrients,
  formatGrams,
  diffReading,
  parseDecimal,
  suggestedPortions,
  toReading,
  formatPortions,
  helpingFor,
  ONE_PORTION,
  useBatches,
  useCorrectFood,
  useFavourites,
  useFoodPortions,
  useFoodSearch,
  useRecentPages,
  useRecipes,
  useSetFavourite,
  useUsuals,
  type CookedBatch,
  type FoodItem,
  type FoodLog,
  type Meal,
  type PortionChip,
  type Reading,
  type Recipe,
  type UsualMeal,
  perHundred,
} from "@calorio/core";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { GramsPad } from "./GramsPad";
import { OptionalDetail } from "./OptionalDetail";
import { portionHint, urgent } from "./pot";
import { ALL_SECTIONS, openSections, setOpenSections, type SheetSection } from "./sheet-sections";
import { ManualFood } from "./ManualFood";
import { MealPicker } from "./MealPicker";
import { closeCamera } from "../lib/camera";
import { ScanPane, TRUST_COPY, type Trust } from "./ScanPane";
import { useOffline } from "../app/offline";
import { useHold } from "../lib/hold";
import { useKeyboard } from "../lib/keyboard";
import { useCalorio } from "../app/providers";

const LABEL: Record<Meal, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snacks",
};
/** Lower case, for mid-sentence use — "Added 2 to lunch". */
const IN_MEAL: Record<Meal, string> = {
  breakfast: "breakfast",
  lunch: "lunch",
  dinner: "dinner",
  snack: "snacks",
};

/**
 * The sheet's resting height, and the strip of day left above it.
 *
 * The ring stays visible over the top of it, which is the entire reason this is
 * a sheet and not a route: you can see what the thing you just logged did to
 * the day without going anywhere to look.
 */
const SHEET_HEIGHT = 560;
const MIN_PEEK = 40;

/**
 * How far the sheet has to be pulled down before letting go closes it, and how
 * fast it has to be going for the distance to stop mattering.
 *
 * Two rules because there are two gestures. A deliberate drag is slow and long;
 * a flick is short and quick, and ends with the finger already off the glass.
 * Distance alone makes the flick feel broken, and velocity alone closes the
 * sheet on a stray swipe that never went anywhere.
 */
const DISMISS_DISTANCE = 96;
const DISMISS_VELOCITY = 0.5;

/**
 * Which day, before which day stops being interesting.
 *
 * Recent pages on forever, and a list that just keeps going implies there is
 * always more. The divider says where the last thirty days stop — the same
 * window the rest of the app reads a habit over.
 */
const RECENT_WINDOW_DAYS = 30;

/**
 * What a portion is, when nobody has said.
 *
 * The food's own default serving first, because a scanned product usually
 * carries one; then whatever the caller knows — a usual carries the weight it
 * was last eaten at; then 100, which is the basis every figure in the system is
 * quoted per and therefore the only defensible guess.
 *
 * Module scope because the list has to *print* the same answer the sheet will
 * *act* on. A second copy of this rule is how a row comes to promise one weight
 * and log another.
 */
function portionOf(food: FoodItem, known?: number): number {
  return Math.round(known ?? food.servings.find((s) => s.is_default)?.grams ?? 100);
}

export type SheetMode =
  | "list"
  | "search"
  | "scan"
  | "confirm"
  | "manual"
  | "quick"
  /** A plateful off the usuals row, opened to be weighed before it is added. */
  | "meal";

/**
 * One thing this sheet has written, for the tray and for undo.
 *
 * Session-scoped on purpose, and correctly so: the tray counts what *this*
 * sheet did and undo takes back the last of it. Whether a food is already on
 * the plate is a different question with a different answer, and it is asked of
 * the day — see `entriesOf`.
 */
interface Written {
  id: string;
  name: string;
  meal: Meal;
  kcal: number;
  /**
   * Which food, and how much of it — so a merge can find the rows this sheet
   * wrote of a food before the day's own list has caught up with them.
   */
  foodId?: string;
  grams?: number;
  /**
   * The plateful this row arrived with, where it arrived with one.
   *
   * A usual meal is one gesture that writes four rows, so undo has to be the
   * same size as the thing it takes back — otherwise adding your usual
   * breakfast by accident costs four presses to undo, and the fourth one
   * silently starts eating rows the meal never wrote.
   */
  group?: string;
}

/**
 * A plateful, opened.
 *
 * The unit the usuals row deals in: several foods, each already carrying the
 * weight this meal last used, held together long enough to be disagreed with.
 * This is the sheet's one piece of state that is not yet written — see the
 * header — and it is deliberately shallow: nothing here has an id, nothing
 * survives the sheet closing, and abandoning it costs nothing because it has
 * changed nothing.
 */
interface Plan {
  /** The combination's own identity, from the server, or the recipe's food. */
  key: string;
  name: string;
  items: { food: FoodItem; grams: number }[];
  /** Where these weights came from, said once, above the list. */
  note?: string;
}

/** A food waiting for a weight, and the story of where it came from. */
interface Picked {
  food: FoodItem;
  /** Absent for a food chosen off a list — nobody read anything to get it. */
  trust?: Trust;
  /** The frame the camera was on when it recognised the packet. */
  still?: string;
}

export interface AddSheetProps {
  meal: Meal;
  /** The day every write from this sheet is filed under. */
  localDate: string;
  /**
   * What is already on that day, so the pad can tell a correction from a second
   * helping.
   *
   * Passed in rather than fetched. Today owns this query and the sheet is a
   * layer over it, so a second `useDay` here would only add an observer — and
   * with `staleTime` at zero that observer refetches on mount, which would
   * overwrite the cache with the server's answer and take any write still
   * sitting in the offline queue out of the day with it. The sheet reads what
   * Today has; the optimistic row is in there the instant it is logged, because
   * `patchDay` puts it there.
   */
  logged: FoodLog[];
  onMeal: (meal: Meal) => void;
  onClose: () => void;
  /** Day one raises this in the mode its three ways in name. */
  initialMode?: SheetMode;
}

/**
 * Pull the sheet down to put it away.
 *
 * The handle at the top says the sheet can be dragged — every bottom sheet on
 * the phone has one and every one of them can — so it had to be true here as
 * well. Until now the only ways out were the × and the backdrop, both of which
 * ask for a thumb at the top of a 560px sheet or a deliberate tap on the strip
 * of day above it.
 *
 * Pointer events rather than touch events, so a mouse on a desktop does the
 * same thing without a second implementation. The pointer is captured on the
 * way down, which is what keeps the gesture alive when the finger leaves the
 * grab strip — and it always does, because the gesture is "drag this downwards
 * off the screen".
 *
 * Upward is clamped rather than resisted. The sheet is already as tall as it is
 * allowed to be, so following a finger upwards would only open a gap under it
 * and show the page through the bottom of the screen.
 */
function useDragToClose(onDismiss: () => void) {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const from = useRef<{ y: number; at: number } | null>(null);

  const finish = (event: React.PointerEvent) => {
    const start = from.current;
    from.current = null;
    setDragging(false);
    if (!start) return;

    const travelled = Math.max(0, event.clientY - start.y);
    const elapsed = event.timeStamp - start.at;
    // Only over a frame or so. Two events in the same millisecond would make
    // this infinite, and an infinite velocity closes the sheet on a tap.
    const speed = elapsed >= 16 ? travelled / elapsed : 0;

    if (travelled >= DISMISS_DISTANCE || (travelled > 24 && speed >= DISMISS_VELOCITY)) {
      // The offset stays. `--sheet-drag` is where the closing animation starts
      // from, so the sheet carries on from the finger rather than snapping back
      // to the top and then sliding away.
      onDismiss();
    } else {
      setOffset(0);
    }
  };

  return {
    offset,
    dragging,
    handlers: {
      onPointerDown(event: React.PointerEvent) {
        // The header is full of controls. A press that starts on one of them is
        // aiming at it, not at the sheet.
        if ((event.target as HTMLElement).closest("button")) return;
        if (event.pointerType === "mouse" && event.button !== 0) return;
        from.current = { y: event.clientY, at: event.timeStamp };
        setDragging(true);
        event.currentTarget.setPointerCapture?.(event.pointerId);
      },
      onPointerMove(event: React.PointerEvent) {
        if (!from.current) return;
        setOffset(Math.max(0, event.clientY - from.current.y));
      },
      onPointerUp: finish,
      onPointerCancel: finish,
    },
  };
}

export function AddSheet({
  meal,
  localDate,
  logged,
  onMeal,
  onClose,
  initialMode = "list",
}: AddSheetProps) {
  const ctx = useCalorio();
  const offline = useOffline();
  const keyboard = useKeyboard();
  const navigate = useNavigate();

  const [mode, setMode] = useState<SheetMode>(initialMode);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<Picked | null>(null);
  const [grams, setGrams] = useState("");
  /** The plateful being weighed, and which line of it the pad is on. */
  const [plan, setPlan] = useState<Plan | null>(null);
  const [weighing, setWeighing] = useState<number | null>(null);
  const [written, setWritten] = useState<Written[]>([]);
  /**
   * The food the last write put beside another of itself, while the offer to
   * merge the two stands. Cleared by either answer; hidden on its own the
   * moment the plate no longer holds two — an undo, a change of meal.
   */
  const [mergeFor, setMergeFor] = useState<FoodItem | null>(null);
  const [switching, setSwitching] = useState(false);
  /** Bumped to thaw a frozen viewfinder; see `ScanPane`. */
  const [scanReset, setScanReset] = useState(0);
  const [scanFrozen, setScanFrozen] = useState(false);
  /** Leaving, but still on screen — the sheet animates out before it unmounts. */
  const [leaving, setLeaving] = useState(false);
  /**
   * Which of the three sections are open, for this meal.
   *
   * Read once per meal rather than on every render, and written on every
   * change, so somebody who never batch-cooks folds Stored away once and gets
   * the sheet they had before — including tomorrow. Null from storage means
   * "never arranged", which is not the same as "arranged to be empty".
   */
  const [sections, setSections] = useState<SheetSection[]>(
    () => openSections(meal) ?? ALL_SECTIONS,
  );
  useEffect(() => setSections(openSections(meal) ?? ALL_SECTIONS), [meal]);
  const toggleSection = (section: SheetSection) => {
    const next = sections.includes(section)
      ? sections.filter((name) => name !== section)
      : [...sections, section];
    setSections(next);
    setOpenSections(meal, next);
  };

  const searchRef = useRef<HTMLInputElement>(null);

  /**
   * Raised straight into search means the keyboard should already be up.
   *
   * Day one's "Search for a food" is a promise about the next thing that
   * happens, and landing on a field somebody then has to tap breaks it. Only on
   * mount, and only when search is where the sheet was opened — focusing on
   * every entry into search mode would fight the user every time they came back
   * to the list.
   */
  useEffect(() => {
    if (initialMode === "search") searchRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usuals = useUsuals(ctx, meal);
  const recipes = useRecipes(ctx);
  const batches = useBatches(ctx);
  const recent = useRecentPages(ctx, meal);
  const results = useFoodSearch(ctx, query);
  const favourites = useFavourites(ctx);
  const setFavourite = useSetFavourite(ctx);
  const correct = useCorrectFood(ctx);

  /**
   * The food the pad is open on, from whichever mode raised it, and the
   * weights this person has logged it at. One query rather than one per pad,
   * because only one pad is ever up — and `null` the rest of the time, which
   * is most of the time, so an open sheet costs nothing until a food is picked.
   */
  const padFood: FoodItem | null =
    picked?.food ?? (plan && weighing !== null ? (plan.items[weighing]?.food ?? null) : null);
  const habits = useFoodPortions(ctx, padFood?.id ?? null);
  const chipsFor = (food: FoodItem): PortionChip[] =>
    suggestedPortions(food, habits.data && padFood?.id === food.id ? habits.data : []);

  const starred = new Set(favourites.data?.map((f: FoodItem) => f.id));

  /**
   * The sheet takes the space above the keyboard, and no more than 560px of it.
   *
   * `MIN_PEEK` keeps a strip of the day showing at every size. A sheet that
   * reached the top of the screen would be a page, and the thing it is not is a
   * page.
   */
  const height = Math.min(SHEET_HEIGHT, Math.max(240, keyboard.available - MIN_PEEK));

  const drop = () => setLeaving(true);
  const drag = useDragToClose(drop);

  /* Escape closes it, like every other layer. Bound on the document rather than
     on the panel because focus is frequently inside a field the panel does not
     contain — the pad's buttons, a native form control — and a keydown handler
     on the sheet would miss both. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLeaving(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  /* The sheet closing is the end of scanning, and the only thing that is.
     `ScanPane` no longer stops the stream when it unmounts — leaving scan for
     the list is a step within scanning, and stopping the camera there is what
     made the browser ask for it again on the way back. So the guarantee moves
     here, to the one event that really does mean the user has finished: the
     indicator goes out when the sheet does. */
  useEffect(() => closeCamera, []);

  /** Put the plateful down. It wrote nothing, so there is nothing to undo. */
  const dropPlan = () => {
    setPlan(null);
    setWeighing(null);
    setGrams("");
  };

  /* Scanning is a mode, so leaving it has to leave the camera too. Unmounting
     `ScanPane` is what stops the stream — that is in its own cleanup — and this
     is only about not stranding a half-typed weight from before the scan. */
  const toScan = () => {
    setPicked(null);
    dropPlan();
    setQuery("");
    setMode("scan");
  };

  const toList = () => {
    setPicked(null);
    dropPlan();
    setMode("list");
  };

  /**
   * Open a plateful, rather than write it.
   *
   * The one tap in this sheet that does not log. A usual meal is several
   * weights at once and the odds of all of them being right today are worse
   * than for any single food — so the pane asks, and the tiles say "Review" so
   * that the tap and the label agree about it.
   */
  const takeMeal = (chosen: Plan) => {
    setPicked(null);
    setPlan(chosen);
    setWeighing(null);
    setGrams("");
    setQuery("");
    setMode("meal");
    (document.activeElement as HTMLElement | null)?.blur();
  };

  /**
   * Every entry of this food already in this meal, on the day being written to,
   * earliest first.
   *
   * Read from the **day**, not from what this sheet happens to have written.
   * The session version was wrong in the ordinary case: log a thing, close the
   * sheet, reopen it, and the list being checked had been thrown away with the
   * component. The plate does not forget when a sheet closes.
   *
   * Plus anything this sheet wrote that the day has not caught up with. The
   * optimistic row is normally in `logged` before the next render, so this is
   * usually nothing — it covers the moment between the write and the cache,
   * and it is matched by id so a row present in both is counted once.
   */
  const entriesOf = (foodId: string): { id: string; grams: number }[] => {
    const onPlate = logged.filter(
      (row) => row.meal === meal && row.food_item_id === foodId && row.quantity_g > 0,
    );
    const known = new Set(onPlate.flatMap((row) => [row.id, row.client_id]));
    const own = written.filter(
      (row) => row.foodId === foodId && row.meal === meal && !known.has(row.id),
    );
    return [
      ...onPlate.map((row) => ({ id: row.id, grams: row.quantity_g })),
      ...own.map((row) => ({ id: row.id, grams: row.grams ?? 0 })),
    ];
  };

  /**
   * Write one row: this food, at exactly this weight.
   *
   * Every path that logs a single food ends here. It notes, *before* writing,
   * whether the food is already on this plate — so the row about to be written
   * is not counted as its own duplicate — and raises the merge offer if it is.
   * A plateful skips the offer; see `logMeal`.
   */
  const write = (food: FoodItem, quantity: number, group?: string) => {
    const per100 = perHundred(food);
    const repeat = !group && entriesOf(food.id).length > 0;
    const id = offline.logFood(
      { food_item_id: food.id, quantity_g: quantity, meal },
      {
        name: food.brand ? `${food.brand} ${food.name}` : food.name,
        meal,
        quantityG: quantity,
        per100,
        localDate,
      },
    );
    setWritten((rows) => [
      ...rows,
      {
        id,
        name: food.name,
        meal,
        kcal: forGrams(per100, quantity).kcal,
        group,
        foodId: food.id,
        grams: quantity,
      },
    ]);
    if (repeat) setMergeFor(food);
  };

  /**
   * Eat one portion out of a pot.
   *
   * The same instant write as everything else in the sheet, through the same
   * queue and into the same tray — a stored tile is a shortcut to a meal, not a
   * different kind of act. What makes it its own queue op rather than a plain
   * log is the other half: the portion also comes out of the fridge, and the
   * two have to be one request or a phone that loses signal between them empties
   * a dish nobody ate.
   *
   * `portions_remaining` moves on screen immediately (see `spendPortion` in the
   * offline provider), because a tile still reading "2 left" under a thumb that
   * just ate one is the reason the tap gets made twice.
   */
  const eatPortion = (batch: CookedBatch) => {
    const helped = helpingFor(batch, ONE_PORTION);
    if (!helped) return;
    const id = offline.logPortion(
      { batchId: batch.id, meal, portions: helped.portions },
      portionHint(batch, helped, localDate),
    );
    setWritten((rows) => [...rows, { id, name: batch.recipe_name, meal, kcal: helped.kcal }]);
  };

  /**
   * Log it, now, at a weight already known.
   *
   * This is the whole claim of the usuals row and of the `+` on each list row:
   * a repeat food is one tap from Today. Nothing is staged, so there is nothing
   * to lose by closing the sheet, and the tray below is the receipt.
   *
   * Weighing something differently is the *other* gesture — the row's body,
   * which opens the pad. Two targets on one row because they are two different
   * intentions and only one of them is common.
   *
   * `portionOf` rounds, because these weights are guesses — a serving, the
   * last sitting's figure — and a guess to the tenth of a gram is a false
   * precision the row would then have to print. A weight typed on the pad
   * goes through `write` directly, unrounded: 12.5 g typed is 12.5 g logged.
   */
  const logNow = (food: FoodItem, known?: number, group?: string) =>
    write(food, portionOf(food, known), group);

  /**
   * Write the plateful, every line of it, at the weights on screen.
   *
   * One write per food rather than one write for the meal, because a meal is
   * not a thing the diary has: Today shows entries, the ring adds up entries,
   * and an entry is a food and a weight. What the sheet keeps is the knowledge
   * that these four arrived together, which is exactly as much as undo needs.
   *
   * No merge offer here, unlike the pad and the `+`. Those write one food, so
   * "that is in lunch twice now" is one question with one answer; a plateful
   * is a repeat of an entire sitting, and raising it for each line would be
   * four questions about a gesture that was one. The rows are on Today either
   * way, where each can be re-weighed or removed.
   */
  const logMeal = () => {
    if (!plan?.items.length) return;
    // Unique within the session, which is all undo needs — it groups rows this
    // component wrote, and this component is the only thing that reads it.
    const group = `${plan.key}#${written.length}`;
    plan.items.forEach((item) => logNow(item.food, item.grams, group));
    dropPlan();
    setMode("list");
  };

  /**
   * A food is chosen off a list. Ask for a weight — do not log it, and do not
   * answer the question either.
   *
   * The pad opens **empty**. Choosing to weigh something is choosing not to
   * accept the default, so the default is the one number that is certainly not
   * wanted — and the chips above the pad still offer it, along with the round
   * portions, which is where "most portions need no typing at all" actually
   * comes from.
   *
   * A scanned product is the other case and keeps its prefill: that number is
   * printed on the packet rather than guessed from a list. See `found`.
   */
  const pick = (food: FoodItem, extra?: Omit<Picked, "food">) => {
    setPicked({ food, ...extra });
    setGrams("");
    // The system keyboard and the pad cannot both be up. Picking opens the pad,
    // so whatever had focus gives it back.
    (document.activeElement as HTMLElement | null)?.blur();
  };

  /**
   * What the pad says about a food that is already on this plate.
   *
   * Said before the button is pressed, because that is where the expectation
   * forms. The old notice promised an amendment; this one promises a second row
   * and names the offer that follows it, so nobody reaches Today expecting one
   * entry and finding two without having been told.
   */
  const already = picked ? entriesOf(picked.food.id) : [];
  const note = picked && already.length > 0
    ? `Already in ${IN_MEAL[meal]} at ${already
        .map((row) => `${formatGrams(row.grams)} ${picked.food.basis_unit}`)
        .join(" + ")}. Add logs it again — you'll be offered a merge.`
    : undefined;

  /** Log what is on the pad, at exactly the weight on the pad. */
  const commit = () => {
    if (!picked) return;
    const quantity = parseDecimal(grams);
    if (!Number.isFinite(quantity) || quantity <= 0) return;
    write(picked.food, quantity);
    setPicked(null);
    setGrams("");
    // A scan ends where it started: aiming at the next packet. A list pick goes
    // back to the list it came from, which it is already on.
    if (mode === "confirm") {
      setScanReset((n) => n + 1);
      setMode("scan");
    }
  };

  /**
   * Fold every entry of one food in this meal into the earliest of them.
   *
   * The earliest keeps its place and its time — it is the row the meal was
   * built around — and the rest are deleted through the same queue that wrote
   * them, so a duplicate that has not reached the server yet is a create that
   * never happens rather than a create and a delete. The survivor's figures are
   * rescaled from the food at the new total, which is what the server does
   * with a weight on its own; sending them alongside only keeps the row on
   * screen from showing the new weight beside the old calories until the queue
   * drains.
   *
   * The tray follows: rows that went are gone from it, and the one that grew,
   * if this sheet wrote it, counts at its new weight. A merge is not undone
   * from the tray — the merged row is one tap away on Today, where it can be
   * re-weighed — and the last thing *added* is still what Undo takes back.
   */
  const merge = () => {
    if (!mergeFor) return;
    const rows = entriesOf(mergeFor.id);
    setMergeFor(null);
    if (rows.length < 2) return;
    const keep = rows[0]!;
    const rest = rows.slice(1);
    const total = rows.reduce((sum, row) => sum + row.grams, 0);
    const per100 = perHundred(mergeFor);
    const macros = forGrams(per100, total);
    offline.updateLog(
      { id: keep.id, local_date: localDate },
      { quantity_g: total, ...macros, ...forGramsNutrients(per100, total) },
    );
    rest.forEach((row) => offline.deleteLog({ id: row.id, local_date: localDate }));
    setWritten((current) =>
      current
        .filter((row) => !rest.some((gone) => gone.id === row.id))
        .map((row) => (row.id === keep.id ? { ...row, kcal: macros.kcal, grams: total } : row)),
    );
  };

  /**
   * Take back the last gesture — which is one row, or a whole plateful.
   *
   * Deletes through the same queue, which collapse against the creates if those
   * have not drained yet — so undoing something logged on a train is a write
   * that never happens rather than two that cancel out.
   *
   * The size of an undo is the size of the thing it undoes. A usual meal is one
   * tap that writes four rows, so pressing Undo once has to take back four;
   * anything else makes the fast path the expensive one to get wrong.
   */
  const undo = () => {
    const last = written.at(-1);
    if (!last) return;
    // A plateful went in as one gesture and comes back out as one. Everything
    // else is a single row, and `group` being absent is what says so.
    const taken = last.group ? written.filter((row) => row.group === last.group) : [last];
    taken.forEach((row) => offline.deleteLog({ id: row.id, local_date: localDate }));
    setWritten((rows) => rows.filter((row) => !taken.includes(row)));
  };

  /**
   * A reader found something. Confirm it, at a weight, with the still beside it.
   *
   * No confirm tap gets us here — a recognised barcode arrives on its own,
   * because the lookup already succeeded and holding the packet up is the only
   * gesture the user made.
   */
  /* Stable, because the list's sentinel subscribes an `IntersectionObserver` to
     it: a new function each render would tear the observer down and build it
     again on every keystroke, and an observer that has just been created fires
     immediately for anything already on screen — which is a fetch per render
     the moment the bottom of the list is visible. */
  const fetchMore = useCallback(() => {
    void recent.fetchNextPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recent.fetchNextPage]);

  const found = useCallback((food: FoodItem, trust: Trust, still?: string) => {
    const serving = food.servings.find((s) => s.is_default);
    setPicked({ food, trust, still });
    setGrams(String(Math.round(serving?.grams ?? 100)));
    setMode("confirm");
  }, []);

  const eaten = written.reduce((sum, row) => sum + row.kcal, 0);
  const oneMeal = written.every((row) => row.meal === written[0]?.meal) ? written[0]?.meal : null;

  /**
   * The one question the sheet asks after a write.
   *
   * Derived from the plate rather than held as a fact, so it answers itself
   * when the plate changes: undo the second row and there is nothing to merge,
   * so there is nothing to ask. Both weights are named, and so is the sum,
   * because the sum is the decision — "merge" is a verb until it has a number.
   */
  const duplicates = mergeFor ? entriesOf(mergeFor.id) : [];
  const offer =
    mergeFor && duplicates.length >= 2 ? (
      <div className="sheet__offer" role="status">
        <span className="sheet__offer-text">
          {mergeFor.name} is in {IN_MEAL[meal]}{" "}
          {duplicates.length === 2 ? "twice" : `${duplicates.length} times`}
          {" — "}
          <span className="num">
            {duplicates
              .map((row) => `${formatGrams(row.grams)} ${mergeFor.basis_unit}`)
              .join(" + ")}
          </span>
          .
        </span>
        <span className="sheet__offer-actions">
          <button className="sheet__offer-merge" onClick={merge}>
            Merge into{" "}
            <span className="num">
              {formatGrams(duplicates.reduce((sum, row) => sum + row.grams, 0))}{" "}
              {mergeFor.basis_unit}
            </span>
          </button>
          <button className="sheet__offer-keep" onClick={() => setMergeFor(null)}>
            {duplicates.length === 2 ? "Keep both" : "Keep them"}
          </button>
        </span>
      </div>
    ) : null;

  const tray =
    written.length > 0 || offer ? (
      <>
        {offer}
        {written.length > 0 && (
          <div className="sheet__tray">
            <span>
              Added <span className="num">{written.length}</span>
              {oneMeal ? ` to ${IN_MEAL[oneMeal]}` : ""} ·{" "}
              <span className="num">{Math.round(eaten).toLocaleString()}</span> kcal
            </span>
            <button className="sheet__undo" onClick={undo}>
              Undo
            </button>
          </div>
        )}
      </>
    ) : null;

  return (
    <div className={`sheet-layer${leaving ? " is-leaving" : ""}`} style={{ bottom: keyboard.inset }}>
      <button className="sheet__backdrop" aria-label="Close" onClick={drop} />

      <section
        className="sheet"
        style={
          {
            height,
            // Followed while the finger is down, sprung back when it lifts, and
            // read by `sheet-drop` as the place to start closing from.
            transform: drag.offset ? `translateY(${drag.offset}px)` : undefined,
            transition: drag.dragging ? "none" : "transform 200ms cubic-bezier(0.2, 0.8, 0.25, 1)",
            "--sheet-drag": `${drag.offset}px`,
          } as React.CSSProperties
        }
        role="dialog"
        aria-modal="true"
        aria-label={`Add to ${IN_MEAL[meal]}`}
        // The exit is an animation rather than an unmount, so this is where the
        // parent finally hears about it. Entry animations end here too, hence
        // the guard.
        onAnimationEnd={() => leaving && onClose()}
      >
        {/* The handle and the header are one grab strip. Everything below them
            either scrolls or is a control, and a sheet that came away in your
            hand when you meant to scroll the list would be worse than one that
            only closes at the ×. */}
        <div className="sheet__grab" {...drag.handlers}>
          <div className="sheet__handle" aria-hidden="true" />

          <header className="sheet__head">
            <button
              className="sheet__meal"
              onClick={() => setSwitching((on) => !on)}
              aria-expanded={switching}
            >
              {LABEL[meal]} <span className="sheet__caret" aria-hidden="true">▾</span>
            </button>

            {mode === "scan" && !scanFrozen && <span className="sheet__mode">Scanning</span>}

            {mode === "scan" ? (
              <button
                className="sheet__action"
                onClick={() => (scanFrozen ? setScanReset((n) => n + 1) : toList())}
              >
                {scanFrozen ? "Take another photo" : "Back to list"}
              </button>
            ) : mode === "confirm" ? (
              <button
                className="sheet__action"
                onClick={() => {
                  setPicked(null);
                  setScanReset((n) => n + 1);
                  setMode("scan");
                }}
              >
                Scan another
              </button>
            ) : mode === "manual" || mode === "quick" || mode === "meal" ? (
              <button className="sheet__action" onClick={toList}>
                Back to list
              </button>
            ) : (
              <button className="sheet__close" onClick={drop} aria-label="Close">
                ×
              </button>
            )}
          </header>
        </div>

        {/* The meal the `+` was pressed under is the answer nearly every time,
            so this is a correction rather than a question — closed until asked
            for, and one tap wide when it is. */}
        {switching && (
          <div className="sheet__meals">
            <MealPicker
              value={meal}
              onChange={(option) => {
                onMeal(option);
                setSwitching(false);
              }}
            />
          </div>
        )}

        {mode === "scan" && (
          <ScanPane
            resetToken={scanReset}
            onFrozen={setScanFrozen}
            onFound={found}
            tray={tray}
          />
        )}

        {mode === "confirm" && picked && (
          <Confirm
            picked={picked}
            grams={grams}
            setGrams={setGrams}
            starred={starred.has(picked.food.id)}
            onStar={(on) => setFavourite.mutate({ foodId: picked.food.id, favourite: on })}
            note={note}
            chips={chipsFor(picked.food)}
            fixing={correct.isPending}
            fix={(changes) =>
              correct
                .mutateAsync({ id: picked.food.id, changes })
                .then((result) => setPicked({ ...picked, food: result.food }))
            }
            onCommit={commit}
            tray={tray}
          />
        )}

        {mode === "manual" && (
          <div className="sheet__form">
            <ManualFood
              initialName={query}
              bare
              onCancel={toList}
              // A food typed here is not logged by saving it — it is picked, and
              // the pad asks what you ate. Saving a food and eating it are two
              // different claims and only one of them was made.
              onCreated={(food) => {
                setMode("list");
                pick(food);
              }}
            />
          </div>
        )}

        {mode === "quick" && <QuickAdd meal={meal} localDate={localDate} onDone={toList} />}

        {mode === "meal" && plan && (
          <>
            <MealPlan
              plan={plan}
              meal={meal}
              onWeigh={(index) => {
                setWeighing(index);
                setGrams(String(Math.round(plan.items[index]!.grams)));
              }}
              onDrop={(index) => {
                setPlan({ ...plan, items: plan.items.filter((_, i) => i !== index) });
                // The pad is aimed at a line by position, and dropping one
                // moves every line after it. Rather than track that, taking a
                // line out ends the weighing — including the case where the
                // line being weighed is the one that just left, which would
                // otherwise leave the sheet showing neither pad nor Add.
                setWeighing(null);
                setGrams("");
              }}
              /* Adding is the pad's job while a line is being weighed — two
                 buttons offering to commit, one of them for a number still
                 being typed, is a way to log 6 g of porridge. */
              onAdd={weighing === null ? logMeal : undefined}
            />

            {tray}

            {weighing !== null && plan.items[weighing] && (
              <GramsPad
                name={plan.items[weighing]!.food.name}
                value={grams}
                onChange={setGrams}
                unit={plan.items[weighing]!.food.basis_unit}
                per100={{
                  kcal: plan.items[weighing]!.food.kcal_per_100,
                  protein_g: plan.items[weighing]!.food.protein_g_per_100,
                  carbs_g: plan.items[weighing]!.food.carbs_g_per_100,
                  fat_g: plan.items[weighing]!.food.fat_g_per_100,
                }}
                chips={chipsFor(plan.items[weighing]!.food)}
                /* "Done", not "Add". The pad is editing a line of something not
                   yet written, and a key labelled Add that writes nothing is the
                   same lie as a tile labelled Add that opens a pane. */
                action="Done"
                onCommit={() => {
                  const quantity = parseDecimal(grams);
                  if (!Number.isFinite(quantity) || quantity <= 0) return;
                  setPlan({
                    ...plan,
                    items: plan.items.map((item, i) =>
                      i === weighing ? { ...item, grams: quantity } : item,
                    ),
                  });
                  setWeighing(null);
                  setGrams("");
                }}
              />
            )}
          </>
        )}

        {(mode === "list" || mode === "search") && (
          <>
            {/* Usuals go when a food is picked. The pad needs the room, and a
                row of shortcuts under a question about weight is an invitation
                to lose the answer you were half way through. */}
            {!picked && mode !== "search" && (
              <Stored
                batches={batches.data}
                open={sections.includes("stored")}
                onToggle={() => toggleSection("stored")}
                onEat={eatPortion}
                // Both leave the sheet for Prepare. Today unmounts on the way,
                // which unmounts the sheet, which is what stops the camera.
                onOpen={(id) => navigate(`/prepare/${id}`)}
                onPrep={() => navigate("/prepare/new")}
              />
            )}

            {!picked && (
              <Usuals
                meal={meal}
                compact={mode === "search"}
                plans={platefuls(usuals.data, recipes.data)}
                open={sections.includes("usuals")}
                onToggle={() => toggleSection("usuals")}
                onTake={takeMeal}
              />
            )}

            <div className="sheet__scroll">
              {mode === "search" ? (
                <Results
                  query={query}
                  results={results.data}
                  loading={results.isLoading}
                  starred={starred}
                  onStar={(id, on) => setFavourite.mutate({ foodId: id, favourite: on })}
                  onWeigh={(food) => pick(food)}
                  onScan={toScan}
                  onByHand={() => setMode("manual")}
                />
              ) : (
                <Recent
                  open={sections.includes("recent")}
                  onToggle={() => toggleSection("recent")}
                  pages={recent.data?.pages}
                  loading={recent.isLoading}
                  fetching={recent.isFetchingNextPage}
                  hasMore={recent.hasNextPage}
                  onEnd={fetchMore}
                  starred={starred}
                  onStar={(id, on) => setFavourite.mutate({ foodId: id, favourite: on })}
                  onWeigh={(food) => pick(food)}
                  onLog={(food) => logNow(food)}
                />
              )}
            </div>

            {tray}

            {picked ? (
              <GramsPad
                name={picked.food.name}
                value={grams}
                onChange={setGrams}
                unit={picked.food.basis_unit}
                per100={{
                  kcal: picked.food.kcal_per_100,
                  protein_g: picked.food.protein_g_per_100,
                  carbs_g: picked.food.carbs_g_per_100,
                  fat_g: picked.food.fat_g_per_100,
                }}
                chips={chipsFor(picked.food)}
                note={note}
                onCommit={commit}
              />
            ) : (
              <div className="sheet__dock">
                <input
                  ref={searchRef}
                  className="sheet__search"
                  value={query}
                  placeholder="Search foods"
                  aria-label="Search foods"
                  onFocus={() => setMode("search")}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setMode("search");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      setQuery("");
                      setMode("list");
                      event.currentTarget.blur();
                    }
                    // The top result on Enter, because the list is ranked and
                    // the top one is what a fast typist means. Nothing is logged
                    // by this — it picks the food and asks for a weight.
                    if (event.key === "Enter" && results.data?.length) pick(results.data[0]!);
                  }}
                />
                <button className="sheet__scan" onClick={toScan}>
                  Scan
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/**
 * A section heading that folds the section under it.
 *
 * Three of them now, which is the point at which a fixed arrangement stops
 * being right for everybody: somebody who batch-cooks on Sunday wants the pots
 * at the top of dinner, and somebody who never has wants that line gone. The
 * state is kept per meal (`sheet-sections.ts`), because breakfast has no pots
 * in it and lunch is nothing but leftovers.
 *
 * The chevron is on the left, ahead of the word, so the three headings line up
 * as a column of controls rather than as three labels that happen to be
 * tappable.
 */
function SectionHead({
  label,
  open,
  onToggle,
  aside,
  sticky,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  /** The count, the "Edit" link — whatever the section says about itself. */
  aside?: ReactNode;
  sticky?: boolean;
}) {
  return (
    <button
      className={`sheet__section-head${sticky ? " is-sticky" : ""}`}
      onClick={onToggle}
      aria-expanded={open}
    >
      <span className="sheet__chev" aria-hidden="true">
        {open ? "▾" : "▸"}
      </span>
      <h2 className="sheet__heading">{label}</h2>
      {aside !== undefined && <span className="sheet__section-aside">{aside}</span>}
    </button>
  );
}

/**
 * What is already cooked, above the usuals.
 *
 * Above them because a pot is perishable and a usual is not: what is going off
 * is what should be eaten, and a rail that buried it under three habitual
 * platefuls would be answering the wrong question first.
 *
 * **Tapping a tile anywhere logs one portion** to the meal in the header and
 * takes it out of the fridge — the same instant write as everything else in the
 * sheet, with the same undo tray. **Holding it** opens the pot instead, which is
 * where the uncommon acts live: rename it, share it, re-count it, throw it out.
 *
 * It was two targets — the name opened the pot, the body ate from it — and the
 * split was invisible: the name is the tile's title in full-weight ink and the
 * body is grey meta text and a 4px bar, so the half that looked tappable was the
 * half that did the rare thing, and the sheet was open in order to log something.
 * One target for the common intention, a hold for the other, the same gesture
 * the fridge's own rows use in the opposite direction (`lib/hold.ts`).
 *
 * The bar under each name is portions left over portions made. It turns amber
 * on the best-before day — the only new use of a hue in this flow, and it is a
 * signal rather than decoration.
 *
 * **Meal prep leads the row rather than trailing it.** With nothing stored it
 * is the only tile, and it is the one thing here you do *before* there is
 * anything to eat. It leads to Prepare — cooking is a route now, not a mode of
 * this sheet — which is also why the section is not hidden when the fridge is
 * empty, despite costing a line: it is the shortcut from a meal to cooking one,
 * and a person who does not batch-cook folds the heading away once and never
 * sees it again.
 */
function Stored({
  batches,
  open,
  onToggle,
  onEat,
  onOpen,
  onPrep,
}: {
  batches: CookedBatch[] | undefined;
  open: boolean;
  onToggle: () => void;
  onEat: (batch: CookedBatch) => void;
  /** Open the pot — its own page under Prepare. */
  onOpen: (id: string) => void;
  /** Cook one — the flow under Prepare. */
  onPrep: () => void;
}) {
  const pots = batches ?? [];
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <section className="stored">
      <SectionHead
        label="Stored"
        open={open}
        onToggle={onToggle}
        aside={
          pots.length > 0 ? (
            <span className="num">
              {pots.length} pot{pots.length === 1 ? "" : "s"}
            </span>
          ) : undefined
        }
      />

      {open && (
        <div className="stored__row">
          <button className="stored__prep" onClick={onPrep}>
            <span className="stored__prep-name">Meal prep</span>
            <span className="stored__prep-note">Cook, divide, share</span>
          </button>

          {pots.map((batch) => (
            <StoredTile
              key={batch.id}
              batch={batch}
              todayIso={todayIso}
              onEat={onEat}
              onOpen={onOpen}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * One pot, as a tile: tap it to eat from it, hold it to go to it.
 *
 * The whole tile is the eat button — see the rail's header for why it stopped
 * being two — so the pot's page is reached by holding, and by a right-click for
 * anybody on a mouse who would never think to hold one. A keyboard has neither
 * gesture, and does not need one: Prepare lists the same pots and its rows are
 * ordinary links, which is where somebody arrives who cannot press and wait.
 */
function StoredTile({
  batch,
  todayIso,
  onEat,
  onOpen,
}: {
  batch: CookedBatch;
  todayIso: string;
  onEat: (batch: CookedBatch) => void;
  onOpen: (id: string) => void;
}) {
  const hold = useHold(() => onOpen(batch.id));
  const share = batch.portions_made > 0 ? batch.portions_remaining / batch.portions_made : 0;
  const isUrgent = urgent(batch, todayIso);

  return (
    <button
      className={`stored__tile${isUrgent ? " is-urgent" : ""}`}
      {...hold.handlers}
      onContextMenu={(event) => {
        event.preventDefault();
        onOpen(batch.id);
      }}
      onClick={() => {
        // The click at the end of a hold. The pot's page is already on its way;
        // logging a portion on top of it would be a meal nobody ate.
        if (hold.held.current) {
          hold.held.current = false;
          return;
        }
        onEat(batch);
      }}
      aria-label={`Log one portion of ${batch.recipe_name}`}
    >
      <span className="stored__top">
        <span className="stored__name">{batch.recipe_name}</span>
        <span className="stored__left num">
          {formatPortions(batch.portions_remaining)} left
        </span>
      </span>
      <span className="stored__meta num">
        {isUrgent
          ? "eat today"
          : [
              batch.weighed && batch.portion_grams
                ? `${Math.round(batch.portion_grams)} g`
                : `1 of ${batch.portions_made}`,
              `${Math.round(batch.kcal_per_portion ?? 0)} kcal`,
            ].join(" · ")}
      </span>
      <span className="stored__bar" aria-hidden="true">
        <span style={{ width: `${Math.max(0, Math.min(1, share)) * 100}%` }} />
      </span>
    </button>
  );
}

/**
 * What the row offers: this meal's habitual platefuls, and the composed meals
 * it knows nothing about.
 *
 * The ranking is not done here, and deliberately. The server groups sittings —
 * the foods logged under one meal on one day — counts how often each
 * combination comes back, and answers with the weights from the latest one. A
 * recipe's food is an ordinary food item owned by its author, so the moment one
 * is eaten it becomes part of whatever plateful it is eaten as, in the meal it
 * is eaten at, and arrives that way. That *is* "rank meals by which meal they
 * are logged into", and it was already running.
 *
 * So a recipe is appended only where the log has nothing to say about it:
 * `recent_logs` counts entries over the same ninety days usuals reads a habit
 * across, and zero is the one case where showing a composed meal under a meal
 * it has never been eaten at is honest. It is new, and it has to be reachable
 * somehow — one tap here, until the first time you eat it decides where it
 * belongs.
 *
 * The dedupe stays regardless of that rule. It is one line, and the failure it
 * prevents — the same meal offered twice at two different weights — is not one
 * to leave resting on a count arriving correctly.
 */
function platefuls(
  usuals: UsualMeal[] | undefined,
  recipes: Recipe[] | undefined,
): Plan[] | undefined {
  if (!usuals && !recipes) return undefined;

  const habits: Plan[] = (usuals ?? []).map((usual) => ({
    key: usual.key,
    name: nameOf(usual.items.map((item) => item.food)),
    items: usual.items.map((item) => ({ food: item.food, grams: item.grams })),
    note: `Eaten ${usual.times} times. These weights are the ones you used on ${onDay(
      usual.last_local_date,
    )} — tap one to change it.`,
  }));
  const known = new Set(habits.flatMap((plan) => plan.items.map((item) => item.food.id)));

  const untried: Plan[] = (recipes ?? [])
    // No food: the server never materialised one, so there is nothing to log.
    // The Foods tab is where that is explained and fixed.
    .filter((recipe) => recipe.food && recipe.recent_logs === 0)
    .filter((recipe) => !known.has(recipe.food!.id))
    .map((recipe) => ({
      key: recipe.food!.id,
      name: recipe.name,
      items: [{ food: recipe.food!, grams: portionOf(recipe.food!) }],
      note: "A meal you saved and have not eaten yet — one portion, unless you say otherwise.",
    }));

  return [...habits, ...untried];
}

/**
 * What to call a plateful nobody has named.
 *
 * A derived usual has no name, only a list, so the list is the name: the foods
 * on it, in the order they were eaten, read as a sentence. Nothing is truncated
 * here — the tile ellipsises what it cannot fit and the assistive label wants
 * the whole thing, and those are two different jobs for one string.
 */
function nameOf(foods: FoodItem[]): string {
  const names = foods.map((food) => food.name);
  if (names.length <= 1) return names[0] ?? "Meal";
  return `${names.slice(0, -1).join(", ")} & ${names.at(-1)}`;
}

/** A local date, in the shortest form that still says which day. */
function onDay(localDate: string): string {
  // Parsed as a local instant rather than UTC: `2026-08-12` through `new Date`
  // is midnight UTC, which is the 11th anywhere west of Greenwich.
  const [year, month, day] = localDate.split("-").map(Number);
  return new Date(year!, month! - 1, day!).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}

/** What a plateful comes to, at the weights currently on it. */
function kcalOf(items: Plan["items"]): number {
  return Math.round(
    items.reduce(
      (sum, item) =>
        sum +
        forGrams(
          {
            kcal: item.food.kcal_per_100,
            protein_g: item.food.protein_g_per_100,
            carbs_g: item.food.carbs_g_per_100,
            fat_g: item.food.fat_g_per_100,
          },
          item.grams,
        ).kcal,
      0,
    ),
  );
}

/**
 * The usuals row — one line of height, however many usuals there are.
 *
 * Sideways rather than a grid, and that is the decision the rest of the sheet
 * is built on: a wrapping grid of six tiles costs three rows, and every one of
 * them comes out of Recent. One row means Recent starts high enough to be a
 * list rather than a peek at one.
 *
 * A tile is a whole meal and does not write. Recent below is where a single
 * food is one tap from the day — the two sections now answer different
 * questions with different gestures, which is the point of both of them.
 */
function Usuals({
  meal,
  compact,
  plans,
  open,
  onToggle,
  onTake,
}: {
  meal: Meal;
  compact: boolean;
  plans: Plan[] | undefined;
  open: boolean;
  onToggle: () => void;
  /** A tile opens its plateful; the pane below it is what writes. */
  onTake: (plan: Plan) => void;
}) {
  // Nothing eaten twice for this meal yet and nothing composed. The row would
  // be a heading over an empty strip, which is worse than the space it takes.
  if (!plans?.length) return null;

  const label = (plan: Plan) =>
    `Review ${plan.name}, ${plan.items.length} ${
      plan.items.length === 1 ? "item" : "items"
    }, ${kcalOf(plan.items)} kcal`;

  if (compact) {
    return (
      <div className="usuals usuals--compact">
        <span className="usuals__label">Usuals</span>
        {plans.map((plan) => (
          <button
            key={plan.key}
            className="usuals__chip"
            aria-label={label(plan)}
            onClick={() => onTake(plan)}
          >
            {plan.name}
          </button>
        ))}
      </div>
    );
  }

  return (
    <section className="usuals">
      <SectionHead
        label={`Your ${IN_MEAL[meal]} usuals`}
        open={open}
        onToggle={onToggle}
      />
      {open && (
      <div className="usuals__row">
        {plans.map((plan) => {
          const single = plan.items.length === 1 ? plan.items[0] : null;
          return (
            <button
              key={plan.key}
              className="usuals__tile"
              // Spelled out, because the tile's own two lines read as "Porridge,
              // Banana & Coffee / 3 items · 512" and the 512 is not 512 of
              // anything a screen reader could name.
              aria-label={label(plan)}
              onClick={() => onTake(plan)}
            >
              <span className="usuals__name">{plan.name}</span>
              <span className="usuals__meta num">
                {/* A plateful of one is a food, and saying "1 item" about it is
                    a count nobody needed. Its weight is the useful half. */}
                {single
                  ? `${Math.round(single.grams)} ${single.food.basis_unit}`
                  : `${plan.items.length} items`}{" "}
                · {kcalOf(plan.items)}
              </span>
            </button>
          );
        })}
      </div>
      )}
    </section>
  );
}

/**
 * A plateful, open, before any of it is written.
 *
 * The whole reason the usuals row stopped writing on the tap. A usual meal is
 * four weights at once, and the odds of all four being right today are worse
 * than for any one of them — so this is where you say the toast was two slices
 * this morning, or that there was no banana, and then add the rest in one go.
 *
 * The lines are the sheet's ordinary row shape on purpose: the body opens the
 * pad on that weight, exactly as a Recent row does, and the button on the right
 * is the one thing that differs — it drops the line instead of adding it,
 * because everything here is already in and the question is what to take out.
 */
function MealPlan({
  plan,
  meal,
  onWeigh,
  onDrop,
  onAdd,
}: {
  plan: Plan;
  meal: Meal;
  onWeigh: (index: number) => void;
  onDrop: (index: number) => void;
  /** Absent while the pad is up: one commit button at a time. */
  onAdd?: () => void;
}) {
  return (
    <>
      <div className="sheet__scroll">
        <h2 className="sheet__heading">{plan.name}</h2>
        {plan.note && <p className="plan__note muted">{plan.note}</p>}

        {plan.items.map((item, index) => {
          // The line's own figures, at the weight on the line — not per 100.
          // This is a plate being checked over, and "193 kcal" is the question
          // it is being checked against.
          const macros = forGrams(perHundred(item.food), item.grams);
          return (
            <div className="sheet__row" key={item.food.id}>
              <button
                className="sheet__row-body"
                aria-label={`Weigh ${item.food.name}, now ${Math.round(item.grams)} ${
                  item.food.basis_unit
                }`}
                onClick={() => onWeigh(index)}
              >
                <span className="sheet__row-title">
                  <span className="entry__name">
                    {item.food.brand && <span className="muted">{item.food.brand} </span>}
                    {item.food.name}
                  </span>
                  <span className="sheet__row-portion num" aria-hidden="true">
                    {Math.round(item.grams)} {item.food.basis_unit}
                  </span>
                </span>
                <span className="entry__meta num">
                  {Math.round(macros.kcal)} kcal · P {Math.round(macros.protein_g)} · C{" "}
                  {Math.round(macros.carbs_g)} · F {Math.round(macros.fat_g)}
                </span>
              </button>
              <button
                className="sheet__row-add plan__drop"
                aria-label={`Leave out ${item.food.name}`}
                onClick={() => onDrop(index)}
              >
                ×
              </button>
            </div>
          );
        })}

        {!plan.items.length && (
          <p className="muted sheet__empty">
            Nothing left on this one — go back to the list, or put a line back by opening it again.
          </p>
        )}
      </div>

      {onAdd && (
        <div className="sheet__dock">
          <button className="button plan__go" disabled={!plan.items.length} onClick={onAdd}>
            Add {plan.items.length} to {IN_MEAL[meal]} · {kcalOf(plan.items)} kcal
          </button>
        </div>
      )}
    </>
  );
}

/**
 * The optional four on the scan sheet's fix form, in the order they are shown.
 *
 * Keyed by what `Reading` calls them rather than by the column, because that
 * is what this form edits — salt is typed in grams here and converted on the
 * way out by `diffReading`.
 */
const FIX_DETAIL = [
  { typed: "fiber", label: "Fibre", unit: "g" },
  { typed: "sugar", label: "Sugar", unit: "g" },
  { typed: "saturates", label: "Saturated fat", unit: "g" },
  { typed: "salt", label: "Salt", unit: "g" },
] as const;

/**
 * One food, in the shape both lists use.
 *
 * How many targets it has depends on whether a portion is already known, which
 * is the difference between the two lists rather than a styling choice.
 *
 * **Recent has three.** The star keeps it; the `+` logs it at the portion this
 * food already carries, which is the one-tap path the sheet exists for; the body
 * opens the pad, which is how you say "the same thing, but 180 g of it". A
 * single target would have to pick one of the last two and be wrong about half
 * the time.
 *
 * **Search results have two**, because there is no portion to log at. These are
 * mostly foods you have never eaten, and the only weight the app could offer is
 * 100 g — the basis the figures are quoted per, which is a unit and not a
 * serving. Committing somebody's diary to a number nobody chose is worse than
 * asking, so here the whole row opens the pad and the `+` is part of it rather
 * than a second button beside it. Two buttons that did the same thing would
 * also read out under the same name, which is its own bug.
 *
 * **Where there are three, the `+` says what it will log.** Two adjacent targets
 * with different outcomes and no signal between them is a guess the user is
 * asked to make every time, and the answer was invisible: a Recent food carries
 * a default serving only if it has one, so for most rows this is 100 g and
 * nothing said so. Printing the weight settles both questions at once — what
 * the `+` commits, and why the row beside it is worth pressing instead.
 *
 * No chevron, deliberately. Rows on Today open an editor with nothing but their
 * own tappability to say so, and the design took the chevron off the meal
 * headers for the same reason: a glyph whose only job is to hint that a line
 * responds to touch ends up on every line.
 */
function FoodRow({
  food,
  starred,
  onStar,
  onWeigh,
  log,
}: {
  food: FoodItem;
  starred: boolean;
  onStar: (on: boolean) => void;
  onWeigh: () => void;
  /**
   * What `+` commits, and the weight it commits at — together, because the
   * label is a promise about the button and the two must not drift apart.
   * Absent where no portion is known, which collapses the row to one target.
   */
  log?: { grams: number; run: () => void };
}) {
  const detail = (
    <>
      <span className="sheet__row-title">
        <span className="entry__name">
          {food.brand && <span className="muted">{food.brand} </span>}
          {food.name}
        </span>
        {/* On the name's line, not beside the pill: the macro line is already
            near full width at 402px, and anything taking room from its right
            wraps "F 2" onto a line of its own.

            Inside the weigh target rather than next to the `+` it describes,
            which turns out to be the better place anyway — the weight is the
            thing you would want to change, so tapping it is how you change it.
            Hidden from the assistive tree because the `+` already announces the
            same figure. */}
        {log && (
          <span className="sheet__row-portion num" aria-hidden="true">
            {log.grams} {food.basis_unit}
          </span>
        )}
      </span>
      <span className="entry__meta num">
        {Math.round(food.kcal_per_100)} kcal / 100 {food.basis_unit} · P{" "}
        {Math.round(food.protein_g_per_100)} · C {Math.round(food.carbs_g_per_100)} · F{" "}
        {Math.round(food.fat_g_per_100)}
      </span>
    </>
  );

  return (
    /* Siblings, not a button inside a button: a nested button is not markup a
       browser has to make sense of, and each of these means something else. */
    <div className="sheet__row">
      <button
        className="entry__star"
        aria-pressed={starred}
        aria-label={starred ? `Unstar ${food.name}` : `Star ${food.name}`}
        onClick={() => onStar(!starred)}
      >
        {starred ? "★" : "☆"}
      </button>

      {log ? (
        <>
          <button className="sheet__row-body" aria-label={`Weigh ${food.name}`} onClick={onWeigh}>
            {detail}
          </button>
          <button
            className="sheet__row-add"
            aria-label={`Add ${food.name}, ${log.grams} ${food.basis_unit}`}
            onClick={log.run}
          >
            +
          </button>
        </>
      ) : (
        /* One target across the row and the pill both. "Add", not "Weigh",
           because adding it is what the user is doing — the weight is the
           question the pad asks on the way, not the point of the gesture. */
        <button className="sheet__row-pick" aria-label={`Add ${food.name}`} onClick={onWeigh}>
          <span className="sheet__row-body">{detail}</span>
          <span className="sheet__row-add" aria-hidden="true">
            +
          </span>
        </button>
      )}
    </div>
  );
}

/**
 * Recent, paging in as you reach the end of it.
 *
 * The sentinel is an element rather than a scroll listener: an observer fires
 * once when the bottom comes into view and costs nothing while it does not,
 * where a listener runs on every frame of every flick to answer the same
 * question.
 */
function Recent({
  open,
  onToggle,
  pages,
  loading,
  fetching,
  hasMore,
  onEnd,
  starred,
  onStar,
  onWeigh,
  onLog,
}: {
  open: boolean;
  onToggle: () => void;
  pages: { items: { food: FoodItem; last_logged_at: string }[] }[] | undefined;
  loading: boolean;
  fetching: boolean;
  hasMore: boolean | undefined;
  onEnd: () => void;
  starred: Set<string>;
  onStar: (id: string, on: boolean) => void;
  onWeigh: (food: FoodItem) => void;
  onLog: (food: FoodItem) => void;
}) {
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onEnd();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, onEnd]);

  const rows = pages?.flatMap((page) => page.items) ?? [];
  const cutoff = Date.now() - RECENT_WINDOW_DAYS * 86_400_000;
  // Where the last thirty days stop, or -1 if the whole list is inside them.
  //
  // Still a single `findIndex` even though the server now leads with this
  // meal's foods in the order they are usually logged rather than by the clock.
  // A seat is only learned from the same thirty days, so nothing above the line
  // can be older than it, and everything below it is still in clock order.
  const boundary = rows.findIndex((row) => Date.parse(row.last_logged_at) < cutoff);

  return (
    <>
      <SectionHead label="Recent" open={open} onToggle={onToggle} sticky />

      {open && loading && <SkeletonRows count={4} />}
      {open && !loading && !rows.length && (
        <p className="muted sheet__empty">
          Nothing logged yet — search for a food, or scan a label.
        </p>
      )}

      {open &&
        rows.map((row, index) => (
        <div key={row.food.id}>
          {/* Said once, where it is true. A list that just keeps scrolling
              implies there is always more of it. */}
          {index === boundary && <div className="sheet__divider">Older than 30 days</div>}
          <FoodRow
            food={row.food}
            starred={starred.has(row.food.id)}
            onStar={(on) => onStar(row.food.id, on)}
            onWeigh={() => onWeigh(row.food)}
            log={{ grams: portionOf(row.food), run: () => onLog(row.food) }}
          />
        </div>
      ))}

      {hasMore && (
        <div ref={sentinel}>
          {/* Rows rather than a spinner: the space the next page will occupy,
              held open, so the list does not jump when it arrives. */}
          {fetching ? <SkeletonRows count={2} /> : <p className="sheet__more">Loading more…</p>}
        </div>
      )}
    </>
  );
}

function SkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div className="sheet__row sheet__row--skeleton" key={index} aria-hidden="true">
          <span className="skeleton skeleton--dot" />
          <span className="sheet__row-body">
            <span className="skeleton skeleton--name" />
            <span className="skeleton skeleton--meta" />
          </span>
          <span className="skeleton skeleton--dot" />
        </div>
      ))}
    </>
  );
}

function Results({
  query,
  results,
  loading,
  starred,
  onStar,
  onWeigh,
  onScan,
  onByHand,
}: {
  query: string;
  results: FoodItem[] | undefined;
  loading: boolean;
  starred: Set<string>;
  onStar: (id: string, on: boolean) => void;
  onWeigh: (food: FoodItem) => void;
  onScan: () => void;
  onByHand: () => void;
}) {
  // Under two characters the search endpoint refuses, so the list is still
  // Recent's job and this says nothing rather than "no results".
  if (query.trim().length < 2) {
    return <p className="muted sheet__empty">Type two letters and the list narrows.</p>;
  }

  return (
    <>
      <h2 className="sheet__heading">Results for “{query.trim()}”</h2>
      {loading && <SkeletonRows count={3} />}
      {results?.map((food) => (
        <FoodRow
          key={food.id}
          food={food}
          starred={starred.has(food.id)}
          onStar={(on) => onStar(food.id, on)}
          onWeigh={() => onWeigh(food)}
        />
      ))}
      {results && (
        /* Offered whether or not anything matched. A near-miss is the commonest
           reason to reach for the camera — the thing in your hand is the
           branded version of the third result. */
        <p className="sheet__nudge">
          Not there?{" "}
          <button className="sheet__inline" onClick={onScan}>
            Scan the label
          </button>{" "}
          or{" "}
          <button className="sheet__inline" onClick={onByHand}>
            enter it by hand
          </button>
          .
        </p>
      )}
    </>
  );
}

/**
 * The confirm state a recognised barcode arrives at on its own.
 *
 * The trust line is the point of it. A barcode match is a lookup of printed
 * data and a label read is a transcription that can misread a digit, and the
 * user has to be able to tell which one they are about to log — so the sentence
 * above the name says which, in words, every time.
 *
 * "Fix" is the one thing here that needs letters, so it opens the field set
 * with the system keyboard and closes the pad. The correction is saved against
 * the *food*, not folded into the entry: entries snapshot their macros either
 * way, so correcting only the entry would leave the product wrong and every
 * future scan of that barcode would repeat the misread.
 */
function Confirm({
  picked,
  grams,
  setGrams,
  starred,
  onStar,
  note,
  chips,
  fixing,
  fix,
  onCommit,
  tray,
}: {
  picked: Picked;
  grams: string;
  setGrams: (value: string) => void;
  starred: boolean;
  onStar: (on: boolean) => void;
  note?: string;
  chips: PortionChip[];
  fixing: boolean;
  fix: (changes: Partial<FoodItem>) => Promise<unknown>;
  onCommit: () => void;
  tray: React.ReactNode;
}) {
  const { food, trust, still } = picked;
  const [edit, setEdit] = useState<Reading | null>(null);
  const pack = food.servings.find((s) => /pack/i.test(s.label));

  if (edit) {
    return (
      <div className="sheet__form">
        <p className="manual__note">
          Fixing these here fixes the product for everyone who scans this barcode, and it is
          audited. Meals already logged keep the macros they were logged with.
        </p>
        <input
          className="field"
          value={edit.name}
          onChange={(e) => setEdit({ ...edit, name: e.target.value })}
          aria-label="Name"
        />
        <label className="manual__row">
          <span>
            Calories<span className="muted"> / 100 {food.basis_unit}</span>
          </span>
          <input
            className="field num manual__value"
            inputMode="decimal"
            value={edit.kcal}
            onChange={(e) => setEdit({ ...edit, kcal: e.target.value })}
            aria-label={`Calories per 100 ${food.basis_unit}`}
          />
        </label>
        {(["protein", "carbs", "fat"] as const).map((macro) => (
          <label className="manual__row" key={macro}>
            <span style={{ textTransform: "capitalize" }}>{macro}</span>
            <input
              className="field num manual__value"
              inputMode="decimal"
              value={edit[macro]}
              onChange={(e) => setEdit({ ...edit, [macro]: e.target.value })}
              aria-label={`${macro} grams per 100 ${food.basis_unit}`}
            />
          </label>
        ))}

        {/* Shut, always. This sheet is held one-handed at a shelf, and the
            reason to be in it is a headline figure that looks wrong. The rest
            of the panel is a thing to fix sitting down. */}
        <OptionalDetail>
          {FIX_DETAIL.map(({ typed, label, unit }) => (
            <label className="manual__row" key={typed}>
              <span>
                {label}
                <span className="muted"> ({unit})</span>
              </span>
              <input
                className="field num manual__value"
                inputMode="decimal"
                value={edit[typed] ?? ""}
                onChange={(e) => setEdit({ ...edit, [typed]: e.target.value })}
                placeholder="—"
                aria-label={`${label} ${unit} per 100 ${food.basis_unit}`}
              />
            </label>
          ))}
        </OptionalDetail>
        <div className="sheet__form-actions">
          <button
            className="button"
            disabled={fixing}
            onClick={() => {
              const changes = diffReading(food, edit);
              if (!Object.keys(changes).length) return setEdit(null);
              void fix(changes).then(() => setEdit(null));
            }}
          >
            {fixing ? "Saving…" : "Save the fix"}
          </button>
          <button className="sheet__inline" onClick={() => setEdit(null)}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="confirm">
        {still && <img className="confirm__still" src={still} alt="The packet you scanned" />}
        <div className="confirm__body">
          <div className="scan-confirm__head">
            <h2 className="confirm__trust">{TRUST_COPY[trust ?? "manual"]}</h2>
            <button
              className="entry__star"
              aria-pressed={starred}
              aria-label={starred ? "Don't save this food" : "Save this food"}
              onClick={() => onStar(!starred)}
            >
              {starred ? "★" : "☆"}
            </button>
          </div>
          <p className="confirm__name">
            {food.brand ? `${food.brand} ${food.name}` : food.name}
          </p>
          <p className="confirm__macros num">
            {Math.round(food.kcal_per_100)} kcal / 100 {food.basis_unit} · P{" "}
            {Math.round(food.protein_g_per_100)} · C {Math.round(food.carbs_g_per_100)} · F{" "}
            {Math.round(food.fat_g_per_100)}
          </p>

          <div className="confirm__fix">
            <span>Numbers wrong? Fixing them here fixes the product for everyone.</span>
            <button
              className="sheet__inline"
              onClick={() => setEdit(toReading(food))}
            >
              Fix
            </button>
          </div>

          {pack && (
            <p className="confirm__pack num">
              Pack: {Math.round(pack.grams)} {food.basis_unit}
            </p>
          )}
        </div>
      </div>

      {tray}

      <GramsPad
        name={food.name}
        value={grams}
        onChange={setGrams}
        unit={food.basis_unit}
        per100={{
          kcal: food.kcal_per_100,
          protein_g: food.protein_g_per_100,
          carbs_g: food.carbs_g_per_100,
          fat_g: food.fat_g_per_100,
        }}
        chips={chips}
        note={note}
        onCommit={onCommit}
      />
    </>
  );
}

/**
 * Calories with no food behind them.
 *
 * Day one's third way in, and the answer to a restaurant meal you know the
 * number of and nothing else about. Macros are optional on purpose: a guessed
 * protein figure is worse than none, because the ring would then split on it.
 */
function QuickAdd({
  meal,
  localDate,
  onDone,
}: {
  meal: Meal;
  localDate: string;
  onDone: () => void;
}) {
  const offline = useOffline();

  return (
    <form
      className="sheet__form"
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const kcal = Number(form.get("kcal"));
        if (!Number.isFinite(kcal) || kcal <= 0) return;
        offline.quickAdd({
          name: String(form.get("name") || "Quick add"),
          kcal,
          protein_g: Number(form.get("protein_g")) || 0,
          carbs_g: Number(form.get("carbs_g")) || 0,
          fat_g: Number(form.get("fat_g")) || 0,
          meal,
          localDate,
        });
        onDone();
      }}
    >
      <p className="manual__note">
        Calories you know without a food behind them. Macros optional — a guessed protein figure is
        worse than none.
      </p>
      <input name="name" placeholder="What was it?" aria-label="Name" className="field" />
      <input
        name="kcal"
        inputMode="numeric"
        placeholder="kcal"
        aria-label="Calories"
        required
        className="field num"
      />
      <div style={{ display: "flex", gap: "var(--sp-2)" }}>
        {(["protein_g", "carbs_g", "fat_g"] as const).map((field) => (
          <input
            key={field}
            name={field}
            inputMode="numeric"
            placeholder={field[0]!.toUpperCase()}
            aria-label={field.replace("_g", "")}
            className="field num"
            style={{ flex: 1 }}
          />
        ))}
      </div>
      <button type="submit" className="button">
        Add to {IN_MEAL[meal]}
      </button>
    </form>
  );
}
